import {
  createContext,
  useCallback,
  useContext,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { cx } from "@/platform/ui";
import { IconEye, IconEyeOff } from "@/platform/ui";

// Global "privacy" toggle: blurs sensitive monetary figures so the dashboard can
// be shown in public without exposing amounts. State persists in localStorage.
interface MaskCtx {
  masked: boolean;
  toggle: () => void;
}
const Ctx = createContext<MaskCtx>({ masked: false, toggle: () => {} });

export function MaskProvider({ children }: { children: ReactNode }) {
  const [masked, setMasked] = useState(() => localStorage.getItem("wp:mask") === "1");
  const toggle = useCallback(() => {
    setMasked((v) => {
      const next = !v;
      localStorage.setItem("wp:mask", next ? "1" : "0");
      return next;
    });
  }, []);
  return <Ctx.Provider value={{ masked, toggle }}>{children}</Ctx.Provider>;
}

const useMask = (): MaskCtx => useContext(Ctx);

// Wrap any sensitive monetary figure. When masking is on the value is replaced
// with a dotted placeholder (not blurred) so the number is fully hidden.
export function Sensitive({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const { masked } = useMask();
  if (masked) {
    return (
      <span className={cx("select-none text-faint", className)} style={style}>
        •••
      </span>
    );
  }
  return (
    <span className={className} style={style}>
      {children}
    </span>
  );
}

// The eye toggle button. Lives on the Portfolio page (not the global header).
export function MaskToggle({ className }: { className?: string }) {
  const { masked, toggle } = useMask();
  return (
    <button
      onClick={toggle}
      className={cx(
        "rounded-lg border border-line bg-surface p-2 text-dim hover:text-ink",
        className,
      )}
      title={masked ? "Show amounts" : "Hide amounts"}
      aria-label={masked ? "Show amounts" : "Hide amounts"}
      aria-pressed={masked}
    >
      {masked ? <IconEyeOff size={18} /> : <IconEye size={18} />}
    </button>
  );
}
